import React, { useState, useEffect, useMemo } from 'react';
import { Flame, Plus, ChevronRight, ShoppingBag } from 'lucide-react';
import { PLATFORMS } from '../data/mock';
import { getMenu, getCategories, resolveImg } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { useToast } from '../hooks/use-toast';
import { useCart } from '../context/CartContext';
import PlatformLogo from '../components/PlatformLogo';
import Seo from '../components/Seo';

export default function Menu() {
  const [menu, setMenu] = useState([]);
  const [categories, setCategories] = useState([]);
  const [active, setActive] = useState('');
  const { toast } = useToast();
  const { add, setOpen: setCartOpen } = useCart();

  useEffect(() => {
    Promise.all([getMenu(), getCategories()]).then(([m, c]) => {
      setMenu(m);
      const catNames = c.map(x => x.name);
      setCategories(catNames);
      setActive(prev => prev || catNames[0] || '');
    });
  }, []);

  const items = useMemo(() => menu.filter(m => m.category === active), [menu, active]);

  const handleAdd = (item) => {
    add(item);
    toast({ title: `${item.name} added to cart`, description: `₹${item.price} — open your cart to checkout.` });
  };

  return (
    <div className="pt-24 pb-20">
      <Seo title="Menu — Burgers, Momos, Pasta & Shakes" path="/menu" description="Explore the Zayka Sphere menu — loaded burgers, peri-peri momos, creamy pasta, crispy fries, thick shakes & mocktails. Order fresh, hot & made-to-order in Lucknow." />
      {/* header */}
      <section className="relative overflow-hidden ember-bg">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-10 left-10 w-72 h-72 bg-orange-600/20 rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-orange-500/10 rounded-full blur-3xl" />
        </div>
        <div className="relative max-w-7xl mx-auto px-5 lg:px-8 py-14 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-orange-500/40 bg-orange-500/10 text-orange-300 text-xs uppercase tracking-[0.25em] mb-4">
            <Flame className="w-3.5 h-3.5" /> Our Menu
          </div>
          <h1 className="font-display text-5xl md:text-7xl leading-none">Every Bite, <span className="text-orange-500 fire-glow">A Crazy Delight</span></h1>
          <p className="mt-5 text-white/60 max-w-2xl mx-auto">Fresh, hot and made to order. Prices are inclusive of standard preparation; taxes may apply.</p>
        </div>
      </section>

      {/* category tabs */}
      <section className="sticky top-20 z-30 bg-black/85 backdrop-blur-md border-y border-orange-500/20">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="flex gap-2 overflow-x-auto py-3 scrollbar-hide">
            {categories.map(cat => (
              <button key={cat} data-testid={`menu-cat-${cat}`} onClick={() => setActive(cat)} className={`px-5 h-10 rounded-full text-sm font-semibold uppercase tracking-widest whitespace-nowrap border transition ${active===cat ? 'bg-orange-500 border-orange-500 text-white' : 'border-white/15 text-white/70 hover:text-orange-400 hover:border-orange-500/50 bg-transparent'}`}>
                {cat}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* items */}
      <section className="py-14">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {items.map((item) => (
              <Card key={item.id} data-testid={`menu-card-${item.id}`} className="food-card bg-neutral-900 border border-white/10 rounded-2xl overflow-hidden p-0">
                <div className="relative h-48 overflow-hidden">
                  <img src={resolveImg(item.image_url)} alt={item.name} className="w-full h-full object-cover img-fade" />
                  <div className="absolute top-3 left-3 bg-orange-500 text-white font-bebas text-lg px-3 py-1 rounded-lg shadow-lg">₹{item.price}</div>
                  {item.is_new && <div data-testid={`new-badge-${item.id}`} className="absolute top-3 right-3 bg-green-600 text-white font-bebas text-sm px-2.5 py-1 rounded-lg shadow-lg animate-pulse">NEW</div>}
                </div>
                <div className="p-5">
                  <h3 className="font-bebas text-2xl tracking-wide">{item.name}</h3>
                  <p className="mt-2 text-sm text-white/60 leading-relaxed min-h-[40px]">{item.desc}</p>
                  <Button data-testid={`add-cart-${item.id}`} onClick={() => handleAdd(item)} className="mt-4 w-full h-11 rounded-full bg-orange-500 hover:bg-orange-600 font-bold uppercase tracking-widest"><Plus className="w-4 h-4 mr-2" /> Add to Cart</Button>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* order platforms */}
      <section id="order-platforms" className="py-16 bg-[#0d0d0d]">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 text-center">
          <h2 className="font-display text-4xl md:text-5xl">Order Zayka Sphere <span className="text-orange-500">Online</span></h2>
          <p className="mt-3 text-white/60">Choose your preferred delivery platform.</p>
          <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {PLATFORMS.map(p => (
              <a key={p.name} href={p.url} target="_blank" rel="noreferrer" className="group relative overflow-hidden rounded-2xl border border-white/10 bg-neutral-900 p-6 flex items-center gap-4 hover:border-orange-500/50 transition text-left">
                <PlatformLogo platform={p} size={56} />
                <div className="flex-1">
                  <div className="font-bebas text-2xl tracking-wide">Order on {p.name}</div>
                  <div className="text-white/50 text-sm">Fast delivery near you</div>
                </div>
                <ChevronRight className="w-5 h-5 text-orange-500 group-hover:translate-x-1 transition" />
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Floating cart CTA */}
      <button onClick={() => setCartOpen(true)} className="fixed bottom-6 right-6 z-40 h-14 px-6 rounded-full bg-orange-500 hover:bg-orange-600 text-white font-bold uppercase tracking-widest shadow-2xl shadow-orange-500/40 flex items-center gap-2 btn-pulse">
        <ShoppingBag className="w-5 h-5" /> View Cart
      </button>
    </div>
  );
}
